import { Trash2, Upload } from "lucide-react";
import { useRef, useState, type FormEvent } from "react";
import { usePageHeader } from "../../components/shell/PageHeaderContext";
import { Spinner } from "../../components/Spinner";
import { apiRequest } from "../../lib/api";
import { uploadPhoto, useAdminMedia, useInvalidateAll } from "../../lib/queries";
import type { MediaKind, MediaPhoto } from "../../types";
import { Feedback, Field, useAction } from "./shared";

const KINDS: Array<{ id: MediaKind; label: string; hint: string }> = [
  { id: "gallery", label: "Galería de acceso", hint: "Fotos que rotan en la pantalla de inicio de sesión." },
  { id: "region", label: "Regiones", hint: "Fondo de las tarjetas de región en Explora." },
  { id: "destination", label: "Destinos", hint: "Foto de cada destino del catálogo." },
  { id: "product", label: "Productos", hint: "Foto de las tarjetas de Finanzas." },
  { id: "dream", label: "Catálogo de sueños", hint: "Fotos que el cliente puede elegir para su wallet." },
  { id: "general", label: "General", hint: "Sin uso específico." }
];

export function AdminMediaPage() {
  usePageHeader("Fotos", "Sube y organiza las fotos que se muestran en la aplicación.");
  const [kind, setKind] = useState<MediaKind>("gallery");
  const [includeCustomer, setIncludeCustomer] = useState(false);
  const media = useAdminMedia(kind, includeCustomer);
  const invalidate = useInvalidateAll();
  const action = useAction();
  const inputRef = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState("");
  const [files, setFiles] = useState<File[]>([]);

  const upload = async (event: FormEvent) => {
    event.preventDefault();
    if (!files.length) return;
    await action.run("upload", async () => {
      for (const [index, file] of files.entries()) {
        await uploadPhoto(file, { admin: true, kind, title: files.length === 1 && title ? title : (title ? `${title} ${index + 1}` : file.name.replace(/\.[^.]+$/, "")) });
      }
      setFiles([]); setTitle("");
      if (inputRef.current) inputRef.current.value = "";
      await invalidate();
      return `${files.length} foto(s) publicadas.`;
    });
  };

  const info = KINDS.find((option) => option.id === kind)!;
  return (
    <div className="page-container">
      <div className="chip-row" role="tablist" aria-label="Tipo de foto">
        {KINDS.map((option) => (
          <button key={option.id} type="button" role="tab" aria-selected={kind === option.id} className={`chip${kind === option.id ? " active" : ""}`} onClick={() => setKind(option.id)}>{option.label}</button>
        ))}
        {kind === "dream" && (
          <label className="check-row inline"><input type="checkbox" checked={includeCustomer} onChange={(event) => setIncludeCustomer(event.target.checked)} /><span>Incluir fotos subidas por clientes</span></label>
        )}
      </div>
      <p className="page-note">{info.hint}</p>

      <form className="card admin-form upload-form" onSubmit={upload}>
        <div className="field-grid">
          <Field label="Archivo(s)" htmlFor="m-file" hint="JPG, PNG o WebP · hasta 8 MB cada una.">
            <input ref={inputRef} id="m-file" type="file" className="standalone-input file" accept="image/png,image/jpeg,image/webp" multiple onChange={(event) => setFiles(Array.from(event.target.files ?? []))} />
          </Field>
          <Field label="Título" htmlFor="m-title" hint="Se muestra al elegir la foto."><input id="m-title" className="standalone-input" value={title} onChange={(event) => setTitle(event.target.value)} maxLength={120} placeholder="Maldivas · atardecer" /></Field>
        </div>
        <Feedback error={action.error} notice={action.notice} />
        <button className="primary-button solid" type="submit" disabled={!files.length || action.busy !== null}>{action.busy === "upload" ? <Spinner /> : <Upload size={16} aria-hidden="true" />} Publicar en {info.label}</button>
      </form>

      {media.isLoading && <div className="page-state"><Spinner label="Cargando fotos" /></div>}
      <div className="media-grid">
        {(media.data ?? []).map((photo) => <MediaTile key={photo.id} photo={photo} />)}
        {media.data && media.data.length === 0 && <div className="empty-state"><strong>Sin fotos en esta categoría</strong><span>Sube la primera con el formulario.</span></div>}
      </div>
    </div>
  );
}

function MediaTile({ photo }: { photo: MediaPhoto }) {
  const invalidate = useInvalidateAll();
  const action = useAction();
  const [title, setTitle] = useState(photo.title ?? "");
  const [sortOrder, setSortOrder] = useState(String(photo.sortOrder));
  const [kind, setKind] = useState<MediaKind>(photo.kind);
  const save = async (extra: Record<string, unknown> = {}) => action.run("save", async () => {
    await apiRequest(`/api/v1/admin/media/${photo.id}`, { method: "PATCH", body: JSON.stringify({ title: title.trim() || null, sortOrder: Number(sortOrder) || 0, kind, ...extra }) });
    await invalidate();
    return "Foto actualizada.";
  });
  const remove = async () => {
    if (!window.confirm(`¿Eliminar “${photo.title ?? "esta foto"}”? Las tarjetas que la usan quedarán sin foto.`)) return;
    await action.run("delete", async () => {
      await apiRequest(`/api/v1/admin/media/${photo.id}`, { method: "DELETE" });
      await invalidate();
    });
  };
  return (
    <article className={`media-tile${photo.active ? "" : " inactive"}`}>
      <div className="media-thumb" style={{ backgroundImage: `url(${photo.url})` }}>
        {photo.ownerId && <span className="glass-pill">Cliente</span>}
        {!photo.active && <span className="glass-pill">Oculta</span>}
      </div>
      <div className="media-fields">
        <input className="standalone-input small" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Título" aria-label="Título" />
        <div className="media-row">
          <select className="standalone-input small" value={kind} onChange={(event) => setKind(event.target.value as MediaKind)} aria-label="Tipo">
            {KINDS.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
          </select>
          <input className="standalone-input small order" inputMode="numeric" value={sortOrder} onChange={(event) => setSortOrder(event.target.value.replace(/\D/g, ""))} aria-label="Orden" title="Orden" />
        </div>
        <span className="field-hint">{photo.width && photo.height ? `${photo.width}×${photo.height} · ` : ""}{Math.round(photo.bytes / 1024)} KB</span>
        <Feedback error={action.error} notice={null} />
        <div className="media-actions">
          <button type="button" className="secondary-button small" onClick={() => void save()} disabled={action.busy !== null}>{action.busy === "save" ? <Spinner /> : "Guardar"}</button>
          <button type="button" className="secondary-button small" onClick={() => void save({ active: !photo.active })} disabled={action.busy !== null}>{photo.active ? "Ocultar" : "Mostrar"}</button>
          <button type="button" className="icon-button danger" onClick={() => void remove()} disabled={action.busy !== null} aria-label="Eliminar foto"><Trash2 size={16} aria-hidden="true" /></button>
        </div>
      </div>
    </article>
  );
}
